import React from "react";
import {useTheme} from "@mui/material";

interface Props {
    width?: number;
    /**
     * Front glyph shown over the hierarchy:
     * - "locked": padlock — used for the root no-access state.
     * - "search": magnifying glass — used for empty / no-result columns.
     */
    variant?: "locked" | "search";
}

/**
 * Illustration for installed-base empty states: a miniature of the list — node
 * rows (icon + title) wired together as a tree — with a glyph in front.
 *
 * With `variant="locked"` a padlock is shown (the user has no access at all);
 * with `variant="search"` a magnifying glass is shown (a column has no entries
 * or a search returned nothing).
 *
 * Colors come from the application theme: the brand primary for the glyph,
 * neutral greys for the rows and connectors.
 */
const InstalledBaseEmptyIllustration = ({width = 240, variant = "locked"}: Props) => {
    const theme = useTheme();

    const backdrop = theme.palette.grey[100];
    const cardFill = theme.palette.common.white;
    const border = theme.palette.grey[300];
    const titleLine = theme.palette.grey[300];
    const subLine = theme.palette.grey[200];
    const primary = theme.palette.primary.main;
    const white = theme.palette.common.white;

    return (
        <svg
            width={width}
            viewBox="0 0 220 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="presentation"
            aria-hidden="true"
        >
            {/* soft backdrop */}
            <circle cx="110" cy="96" r="92" fill={backdrop} />

            {/* connectors wiring the rows into a tree */}
            <g stroke={border} strokeWidth="2.5" strokeLinecap="round">
                <path d="M110 50 V66" />
                <path d="M57 66 H163" />
                <path d="M57 66 V82" />
                <path d="M163 66 V82" />
            </g>

            {/* root row */}
            <rect x="78" y="20" width="64" height="30" rx="6" fill={cardFill} stroke={border} strokeWidth="1.5" />
            <rect x="84" y="26" width="18" height="18" rx="4" fill={border} />
            <rect x="108" y="28" width="26" height="5" rx="2.5" fill={titleLine} />
            <rect x="108" y="37.5" width="16" height="4" rx="2" fill={subLine} />

            {/* left child row */}
            <rect x="28" y="82" width="58" height="28" rx="6" fill={cardFill} stroke={border} strokeWidth="1.5" />
            <rect x="34" y="88" width="16" height="16" rx="4" fill={border} />
            <rect x="56" y="90" width="22" height="4.5" rx="2" fill={titleLine} />
            <rect x="56" y="98" width="14" height="4" rx="2" fill={subLine} />

            {/* right child row */}
            <rect x="134" y="82" width="58" height="28" rx="6" fill={cardFill} stroke={border} strokeWidth="1.5" />
            <rect x="140" y="88" width="16" height="16" rx="4" fill={border} />
            <rect x="162" y="90" width="22" height="4.5" rx="2" fill={titleLine} />
            <rect x="162" y="98" width="14" height="4" rx="2" fill={subLine} />

            {/* halo so the glyph reads as being in front of the structure */}
            <circle cx="110" cy="140" r="37" fill={white} />

            {variant === "locked" ? (
                <>
                    {/* padlock */}
                    <path
                        d="M95 133 v-10 a15 15 0 0 1 30 0 v10"
                        stroke={primary}
                        strokeWidth="4.5"
                        strokeLinecap="round"
                    />
                    <rect x="88" y="132" width="44" height="34" rx="7" fill={primary} />
                    <circle cx="110" cy="145" r="4.5" fill={white} />
                    <path d="M110 148 v8" stroke={white} strokeWidth="4" strokeLinecap="round" />
                </>
            ) : (
                <>
                    {/* magnifying glass */}
                    <circle cx="105" cy="135" r="15" fill={white} stroke={primary} strokeWidth="5" />
                    <path d="M116 146 L131 161" stroke={primary} strokeWidth="6" strokeLinecap="round" />
                </>
            )}
        </svg>
    );
};

export default InstalledBaseEmptyIllustration;
